import { motion } from 'framer-motion';
import { 
  Brain, 
  Lock, 
  Gauge, 
  Coins, 
  Globe, 
  LineChart,
  Zap
} from 'lucide-react';

const features = [
  {
    icon: Brain,
    title: 'Kronos AI 模型',
    description: '首个开源金融 K 线基础模型，基于 45+ 全球交易所数据训练',
    color: 'purple',
  },
  {
    icon: Lock,
    title: 'x402 安全协议',
    description: '基于 HTTP 402 的去中心化微支付协议，无需信任第三方',
    color: 'blue',
  },
  {
    icon: Gauge,
    title: 'Monad 高性能',
    description: '10,000+ TPS，毫秒级确认时间，极低的交易费用',
    color: 'green',
  },
  {
    icon: Coins,
    title: '微支付友好',
    description: '单次查询仅需 0.01 MON，按需付费，无订阅绑定',
    color: 'yellow',
  },
  {
    icon: Globe,
    title: '多币种支持',
    description: '支持 BTC、ETH、SOL、AVAX 等主流加密货币预测',
    color: 'cyan',
  },
  {
    icon: LineChart,
    title: '多时间周期',
    description: '5分钟到1天，灵活选择适合你交易策略的时间周期',
    color: 'pink',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function Features() {
  return (
    <section className="py-24 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            为什么选择我们
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            结合前沿 AI 技术、去中心化支付协议和高性能区块链
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyber-purple/20 to-cyber-blue/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative glass rounded-2xl p-8 h-full border border-transparent group-hover:border-cyber-purple/30 transition-all duration-300">
                {/* 图标 */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getGradient(feature.color)} flex items-center justify-center mb-6`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>

                {/* 内容 */}
                <h3 className="text-xl font-bold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {feature.description}
                </p>

                {/* 装饰角标 */}
                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-cyber-purple/30 rounded-tr-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-cyber-blue/30 rounded-bl-lg opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* 底部统计 */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          <StatCard value="45+" label="数据源交易所" icon={Globe} />
          <StatCard value="0.01" label="MON / 查询" icon={Coins} />
          <StatCard value="120" label="最大预测周期" icon={LineChart} />
          <StatCard value="<1s" label="响应时间" icon={Zap} />
        </motion.div>
      </div>
    </section>
  );
}

function StatCard({ value, label, icon: Icon }: { value: string; label: string; icon: any }) {
  return (
    <div className="glass rounded-2xl p-6 text-center">
      <Icon className="w-6 h-6 text-cyber-purple mx-auto mb-3" />
      <div className="font-display text-3xl font-bold bg-gradient-to-r from-cyber-purple to-cyber-blue bg-clip-text text-transparent mb-1">
        {value}
      </div>
      <div className="text-sm text-gray-400">{label}</div>
    </div>
  );
}

function getGradient(color: string) {
  const gradients: Record<string, string> = {
    purple: 'from-cyber-purple to-purple-700',
    blue: 'from-cyber-blue to-blue-700',
    green: 'from-cyber-green to-green-700',
    yellow: 'from-cyber-yellow to-yellow-700',
    cyan: 'from-cyan-500 to-cyan-700',
    pink: 'from-pink-500 to-pink-700',
  };
  return gradients[color] || gradients.purple;
}

